/**
 * How long a video file runs, measured in the only place that can.
 *
 * ## Why this exists
 *
 * Sibling of `imageSize`, for the same reason and with the same shape: the
 * server has no decoder, so if the browser does not measure the file nobody
 * ever does. `findings.ts` reported `duration_missing` at a partner who had no
 * field to answer it with — and even once the field existed, asking a supplier
 * to count the seconds of a file they just handed over is asking them to
 * transcribe something the browser already knows.
 *
 * So the master upload measures it and offers the answer. It is an OFFER, not
 * a fact: the caller fills a blank field and never overwrites a value a human
 * typed, because a container's metadata can be wrong and a person correcting it
 * must not be corrected back.
 *
 * ## 🚨 The timeout is not defensive padding
 *
 * Exactly the lesson `imageSize` records: awaiting `loadedmetadata`/`error` and
 * nothing else never settles in jsdom, and a stalled demuxer reaches the same
 * state in a real browser. An unmeasurable file is a NORMAL outcome — the
 * catalogue is full of titles with no duration — so every unhappy path resolves
 * null and the upload proceeds untouched.
 */

/** Generous for a demux, short enough that nobody watches a dead dialog. */
export const DURATION_TIMEOUT_MS = 5_000;

/**
 * How a video gets demuxed. Injected so the timeout and the null-paths can be
 * tested without a real decoder — the loader is the only impure part.
 */
export type DurationLoader = (file: File) => Promise<number | null>;

/** The real one: a detached `<video>` pointed at an object URL. */
export const domDurationLoader: DurationLoader = (file) =>
  new Promise((resolve) => {
    let url: string;
    try {
      url = URL.createObjectURL(file);
    } catch {
      // No object URLs here (an older embedded webview, a locked-down
      // environment). Unmeasured, not failed.
      resolve(null);
      return;
    }

    const video = document.createElement("video");
    const done = (seconds: number | null) => {
      URL.revokeObjectURL(url);
      resolve(seconds);
    };
    // `preload="metadata"` so a two-gigabyte master is not pulled through the
    // decoder to learn one number.
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      // A live or unseekable stream reports `Infinity`; a container that did
      // not record it reports `NaN`. Both are "unmeasured", not zero — and
      // zero is a real answer for a live channel, so it must not be invented.
      done(Number.isFinite(seconds) && seconds > 0 ? seconds : null);
    };
    video.onerror = () => done(null);
    video.src = url;
  });

/**
 * Measure `file` in whole seconds, or answer null — never hang, never throw.
 *
 * ⚠️ The object URL is revoked by the loader on BOTH of its paths, for the
 * reason `imageSize` spells out: a leaked blob URL pins the file in memory for
 * the life of the document, and a master is measured in gigabytes rather than
 * megabytes. The timeout does NOT revoke — the loader still owns that url and
 * may still settle, and revoking underneath it would be a second bug.
 */
export async function measureVideoDuration(
  file: File,
  load: DurationLoader = domDurationLoader,
  timeoutMs: number = DURATION_TIMEOUT_MS
): Promise<number | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const seconds = await Promise.race([
      load(file).catch(() => null),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
    return seconds === null ? null : Math.round(seconds);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Seconds -> the minutes a human reads, or "" when there is nothing to show.
 *
 * ⚠️ Rounded for DISPLAY only. The stored value stays in seconds and is never
 * rewritten from what this returns unless somebody edits the field — otherwise
 * opening a title with 5 510 seconds and saving it would quietly restate it as
 * 5 520, and a lossy round-trip on every open is how catalogues drift.
 */
export function secondsToMinutes(seconds: string): string {
  const value = Number(seconds);
  if (!seconds.trim() || !Number.isFinite(value) || value <= 0) return "";
  return String(Math.round(value / 60));
}

/** The minutes a human typed -> the seconds the catalogue stores. */
export function minutesToSeconds(minutes: string): string {
  const value = Number(minutes);
  if (!minutes.trim() || !Number.isFinite(value) || value < 0) return "";
  return String(Math.round(value * 60));
}
